import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertCircle, ArrowUpRight, Loader2, Copy, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading | success | pending | error
  const [message, setMessage] = useState("");
  const [details, setDetails] = useState(null);
  const pollRef = useRef(null);

  const sessionId = params.get("session_id");
  const legacyCode = params.get("code") || params.get("entreprise_code") || "";
  const legacyPeriod = params.get("period") || "annual";

  // -- MODE 1 : nouveau flow SUBSCRIPTION (session_id) --
  useEffect(() => {
    if (!sessionId) return; // legacy handled below

    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 15; // ~30s

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/public/subscription/session/${sessionId}`);
        setDetails(data);
        if (data.payment_status === "paid") {
          setStatus("success");
          setMessage(
            data.cycle === "annual"
              ? `Votre abonnement annuel est actif · Prochaine échéance : dans 12 mois (renouvellement automatique).`
              : `Votre abonnement mensuel est actif · Prochaine échéance : dans 1 mois (renouvellement automatique).`,
          );
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setStatus("pending");
          setMessage("Le paiement est en cours de traitement. Vous recevrez un email dès validation.");
          return;
        }
        pollRef.current = setTimeout(poll, 2000);
      } catch (e) {
        setStatus("error");
        setMessage(e.response?.data?.detail || "Impossible de vérifier le paiement.");
      }
    };
    poll();

    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [sessionId]);

  // -- MODE 2 : legacy flow avec ?code=&period= (compat) --
  useEffect(() => {
    if (sessionId) return; // handled above
    if (!legacyCode) {
      setStatus("error");
      setMessage("Aucun identifiant fourni. Veuillez nous contacter.");
      return;
    }
    api.post("/payment/confirm", { entreprise_code: legacyCode, period: legacyPeriod })
      .then((r) => {
        setDetails(r.data);
        if (r.data.status === "activated") {
          setStatus("success");
          setMessage(`Votre entreprise est activée pour ${legacyPeriod === "annual" ? "12 mois" : "1 mois"}.`);
        } else {
          setStatus("pending");
          setMessage(r.data.message || "Paiement reçu, en attente de validation admin.");
        }
      })
      .catch((e) => {
        setStatus("error");
        setMessage(e.response?.data?.detail || "Une erreur est survenue.");
      });
  }, [sessionId, legacyCode, legacyPeriod]);

  const Icon = status === "success" ? CheckCircle2 : status === "pending" ? AlertCircle : status === "loading" ? Loader2 : AlertCircle;
  const bgColor = status === "success" ? "#059669" : status === "pending" ? "#FFD700" : status === "loading" ? "#002FA7" : "#E60000";
  const textColor = status === "pending" ? "#0A0A0A" : "white";

  const copy = (val) => { navigator.clipboard.writeText(val || ""); toast.success("Copié dans le presse-papier"); };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="border-b border-[#DEE2E6]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/pag-logo.png" alt="PAG" className="h-10 w-10 object-contain" />
            <span className="font-display font-black text-xl tracking-tight">PAG</span>
          </Link>
          <Link to="/" className="text-sm font-semibold hover:text-[#002FA7]">← Accueil</Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-xl" data-testid="payment-result">
          <div
            className="p-10 border border-[#0A0A0A]"
            style={{ backgroundColor: bgColor, color: textColor }}
            data-testid={
              status === "success" ? "payment-success-paid" :
              status === "pending" ? "payment-success-pending" :
              status === "error" ? "payment-success-error" :
              "payment-success-loading"
            }
          >
            <Icon className={`h-12 w-12 mb-6 ${status === "loading" ? "animate-spin" : ""}`} />
            <div className="text-xs uppercase tracking-[0.3em] font-semibold opacity-80 mb-3">
              {status === "success" && "// Abonnement activé"}
              {status === "pending" && "// En attente de validation"}
              {status === "error" && "// Erreur"}
              {status === "loading" && "// Traitement…"}
            </div>
            <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter leading-[1] mb-6">
              {status === "success" && "Bienvenue sur PAG."}
              {status === "pending" && "Paiement reçu, merci."}
              {status === "error" && "Oups..."}
              {status === "loading" && "Nous vérifions votre paiement…"}
            </h1>
            <p className="text-base lg:text-lg leading-relaxed opacity-90 mb-6">{message}</p>

            {/* Détails abonnement (mode subscription — nouveaux identifiants) */}
            {status === "success" && details?.entreprise_code && (
              <div className="pt-6 mt-2 border-t border-current/20 space-y-3 font-mono text-sm" data-testid="new-credentials">
                <div className="text-xs uppercase tracking-[0.3em] opacity-70 mb-2">// Vos identifiants</div>
                <div className="flex items-center justify-between gap-3 bg-black/25 px-4 py-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest opacity-60">Code entreprise</div>
                    <div className="font-bold text-lg">{details.entreprise_code}</div>
                  </div>
                  <button onClick={() => copy(details.entreprise_code)} data-testid="copy-code" className="p-2 hover:bg-white/10">
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
                {details.chef_password && (
                  <div className="flex items-center justify-between gap-3 bg-black/25 px-4 py-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest opacity-60">Mot de passe (à changer)</div>
                      <div className="font-bold text-lg flex items-center gap-2"><KeyRound className="h-4 w-4" />{details.chef_password}</div>
                    </div>
                    <button onClick={() => copy(details.chef_password)} data-testid="copy-password" className="p-2 hover:bg-white/10">
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                )}
                <div className="text-[11px] opacity-70">
                  Notez ces identifiants — vous en aurez besoin pour vous connecter. Le mot de passe pourra être modifié depuis votre espace.
                </div>
              </div>
            )}

            {/* Legacy due_date display */}
            {details?.due_date && (
              <div className="pt-6 border-t border-current/20 font-mono text-sm">
                Prochaine échéance : {new Date(details.due_date).toLocaleDateString("fr-FR")}
              </div>
            )}
          </div>

          {status !== "loading" && (
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/login/chef" className="inline-flex items-center h-12 px-6 bg-[#002FA7] hover:bg-[#002175] text-white font-semibold text-sm transition-colors" data-testid="success-login">
                Me connecter <ArrowUpRight className="ml-2 h-4 w-4" />
              </Link>
              <a href="mailto:contact@parcavenue.eu" className="inline-flex items-center h-12 px-6 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white font-semibold text-sm transition-colors">
                Nous contacter
              </a>
            </div>
          )}

          <div className="mt-8 text-xs text-[#868E96]">
            {sessionId
              ? <>Session Stripe : <span className="font-mono font-bold">{sessionId.slice(0, 20)}…</span> · Cycle : <span className="font-bold">{details?.cycle === "monthly" ? "Mensuel" : "Annuel"}</span> · Renouvellement automatique</>
              : <>Code entreprise : <span className="font-mono font-bold">{legacyCode || "—"}</span> · Période : <span className="font-bold">{legacyPeriod === "annual" ? "Annuelle (12 mois)" : "Mensuelle"}</span></>}
          </div>
        </div>
      </main>
    </div>
  );
}
